/**
 * The front of a card as a PNG: the picture the Share button sends.
 *
 * Drawn on a canvas rather than captured from the page, because Safari taints
 * a canvas that draws HTML through an SVG foreignObject, so a capture of the
 * live card cannot be exported on an iPhone. The image repeats the landscape
 * face of business-card.tsx at a fixed 1700 px width (85 mm at 20 px/mm) in
 * the same container units (1cqw = 17 px): the primary logo top-left with its
 * exclusion zone on the margin, the name in Turbine, job title, e-mail and
 * phone, the rule, the company block bottom-left and norican.com
 * bottom-right, everything Norican Blue on white. The texts come from
 * `cardFace`, as on the card.
 *
 * The QR carries the full vCard, the symbol of the QR dialog and the
 * downloads: the image has room the 560 px face does not. Its modules are
 * whole pixels, so the symbol stays sharp when a messenger shows the image.
 * Text that does not fit its column wraps, and a face that outgrows 85:55
 * makes the image taller, as the card does.
 */

import type { CardProfile } from "@/lib/api";
import { cardFace } from "@/lib/card-face";
import { chooseDialogSymbol } from "@/lib/card-qr";
import type { QrMatrix } from "@/lib/qr";

/** Norican Blue, the face's only colour (brand guideline palette). */
const NORICAN_BLUE = "#03193d";
const LOGO_URL = "/brand/logo.svg";

/* The numbers below mirror the landscape classes in business-card.tsx. */
const WIDTH = 1700;
const CQW = WIDTH / 100;
const MIN_HEIGHT = (WIDTH * 55) / 85;
const PAD_TOP = 4.6 * CQW;
const PAD_RIGHT = 4.8 * CQW;
const PAD_BOTTOM = 4.6 * CQW;
const PAD_LEFT = 5.8 * CQW;
const GAP_X = 2.8 * CQW;
const GAP_Y = 2.4 * CQW;
const QR_COLUMN = 28.5 * CQW;
/** The logo box and the exclusion zone it carries (21.56 of 345.78 units on every side). */
const LOGO_HEIGHT = 13 * CQW;
const LOGO_INSET = 2.34 * CQW;
const LOGO_ASPECT = 345.78 / 119.97;
const NAME_SIZE = 5.4 * CQW;
const NAME_LEADING = 0.96;
const BODY_SIZE = 2.9 * CQW;
const COMPANY_SIZE = 2.6 * CQW;
const BODY_LEADING = 1.2;
const TITLE_GAP = 0.8 * CQW;
/** The rule is 1 css px on the 560 px card. */
const RULE_HEIGHT = Math.round(WIDTH / 560);

interface Line {
  text: string;
  font: string;
}

/** The card front of `card` as a PNG. */
export async function cardImageBlob(card: CardProfile): Promise<Blob> {
  const fonts = faceFonts();
  await Promise.all(Object.values(fonts).map(loadFont));
  const logo = await loadLogo();

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is not available");
  }

  const face = cardFace(card);
  const matrix = chooseDialogSymbol(card.vcard).matrix;
  const modulePx = Math.max(1, Math.floor(QR_COLUMN / matrix.size));
  const qrEdge = matrix.size * modulePx;
  const textWidth = WIDTH - PAD_LEFT - PAD_RIGHT - GAP_X - Math.max(QR_COLUMN, qrEdge);
  const nameLineHeight = NAME_SIZE * NAME_LEADING;
  const bodyLineHeight = BODY_SIZE * BODY_LEADING;
  const companyLineHeight = COMPANY_SIZE * BODY_LEADING;

  const name = wrap(context, fonts.name, face.name, textWidth);
  const title = face.jobTitle ? wrap(context, fonts.body, face.jobTitle, textWidth) : [];
  const contact = [
    ...wrapEmail(context, fonts.body, face.email, textWidth),
    ...(face.phone
      ? wrap(context, fonts.body, `${face.phone.prefix} ${face.phone.value}`, textWidth)
      : []),
  ];
  const company: Line[] = [
    { text: face.company, font: fonts.companyBold },
    ...[face.street, face.cityLine, face.country].flatMap((text) =>
      text
        ? wrap(context, fonts.company, text, textWidth).map((wrapped) => ({
            text: wrapped,
            font: fonts.company,
          }))
        : [],
    ),
  ];
  const website: Line = { text: face.website, font: fonts.bodyBold };

  // Content row: logo / identity / contact / rule spaced evenly over the
  // height of the QR beside them, at least GAP_Y apart (justify-between).
  const logoFlow = LOGO_HEIGHT - LOGO_INSET;
  const identityHeight =
    name.length * nameLineHeight + (title.length ? TITLE_GAP + title.length * bodyLineHeight : 0);
  const contactHeight = contact.length * bodyLineHeight;
  const columnContent = logoFlow + identityHeight + contactHeight + RULE_HEIGHT;
  const contentRow = Math.max(qrEdge, columnContent + 3 * GAP_Y);
  const spacing = (contentRow - columnContent) / 3;

  // Company row: the company block and norican.com share their last baseline.
  const companyLastBaseline =
    (company.length - 1) * companyLineHeight +
    baselineInLine(context, company.at(-1)?.font ?? fonts.companyBold, companyLineHeight);
  const websiteBaseline = baselineInLine(context, website.font, bodyLineHeight);
  const rowBaseline = Math.max(companyLastBaseline, websiteBaseline);
  const companyTop = rowBaseline - companyLastBaseline;
  const websiteTop = rowBaseline - websiteBaseline;
  const companyRow = Math.max(
    companyTop + company.length * companyLineHeight,
    websiteTop + bodyLineHeight,
  );

  const height = Math.ceil(
    Math.max(MIN_HEIGHT, PAD_TOP + contentRow + GAP_Y + companyRow + PAD_BOTTOM),
  );
  canvas.width = WIDTH;
  canvas.height = height;
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, WIDTH, height);
  context.fillStyle = NORICAN_BLUE;

  context.drawImage(
    logo,
    PAD_LEFT - LOGO_INSET,
    PAD_TOP - LOGO_INSET,
    LOGO_HEIGHT * LOGO_ASPECT,
    LOGO_HEIGHT,
  );

  let y = PAD_TOP + logoFlow + spacing;
  for (const text of name) {
    drawLine(context, { text, font: fonts.name }, PAD_LEFT, y, nameLineHeight);
    y += nameLineHeight;
  }
  if (title.length) {
    y += TITLE_GAP;
  }
  for (const text of title) {
    drawLine(context, { text, font: fonts.body }, PAD_LEFT, y, bodyLineHeight);
    y += bodyLineHeight;
  }
  y += spacing;
  for (const text of contact) {
    drawLine(context, { text, font: fonts.body }, PAD_LEFT, y, bodyLineHeight);
    y += bodyLineHeight;
  }
  context.fillRect(PAD_LEFT, PAD_TOP + contentRow - RULE_HEIGHT, textWidth, RULE_HEIGHT);

  drawQr(context, matrix, Math.round(WIDTH - PAD_RIGHT - qrEdge), Math.round(PAD_TOP), modulePx);

  const rowTop = height - PAD_BOTTOM - companyRow;
  company.forEach((line, index) =>
    drawLine(
      context,
      line,
      PAD_LEFT,
      rowTop + companyTop + index * companyLineHeight,
      companyLineHeight,
    ),
  );
  drawLine(context, website, WIDTH - PAD_RIGHT, rowTop + websiteTop, bodyLineHeight, "right");

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("PNG export failed"))),
      "image/png",
    );
  });
}

/** Canvas font strings in the design-system families (Turbine for the name, Manrope for the rest). */
function faceFonts() {
  const style = getComputedStyle(document.documentElement);
  const display = style.getPropertyValue("--norican-font-family-display").trim() || "sans-serif";
  const sans = style.getPropertyValue("--norican-font-family-sans").trim() || "sans-serif";
  return {
    name: `400 ${NAME_SIZE}px ${display}`,
    body: `400 ${BODY_SIZE}px ${sans}`,
    bodyBold: `700 ${BODY_SIZE}px ${sans}`,
    company: `400 ${COMPANY_SIZE}px ${sans}`,
    companyBold: `700 ${COMPANY_SIZE}px ${sans}`,
  };
}

/** A canvas does not wait for webfonts; load each face first (a failure leaves the fallback). */
async function loadFont(font: string): Promise<void> {
  try {
    await document.fonts.load(font);
  } catch {
    // The fallback family still draws the text.
  }
}

/**
 * The official primary logo with an explicit pixel size: an SVG without
 * width and height draws nothing on a canvas in some engines.
 */
async function loadLogo(): Promise<HTMLImageElement> {
  const response = await fetch(LOGO_URL);
  if (!response.ok) {
    throw new Error(`Could not load the logo (${response.status})`);
  }
  const svg = (await response.text()).replace(
    "<svg ",
    `<svg width="${Math.round(LOGO_HEIGHT * LOGO_ASPECT)}" height="${Math.round(LOGO_HEIGHT)}" `,
  );
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Could not draw the logo"));
      image.src = url;
    });
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Distance from the top of a line box to its baseline, as CSS places it (half-leading above the ascent). */
function baselineInLine(
  context: CanvasRenderingContext2D,
  font: string,
  lineHeight: number,
): number {
  context.font = font;
  const metrics = context.measureText("Hg");
  const size = Number.parseFloat(font.split(" ")[1] ?? "0");
  const ascent = metrics.fontBoundingBoxAscent ?? size * 0.8;
  const descent = metrics.fontBoundingBoxDescent ?? size * 0.2;
  return (lineHeight - ascent - descent) / 2 + ascent;
}

function drawLine(
  context: CanvasRenderingContext2D,
  line: Line,
  x: number,
  top: number,
  lineHeight: number,
  align: CanvasTextAlign = "left",
): void {
  const baseline = top + baselineInLine(context, line.font, lineHeight);
  context.font = line.font;
  context.textAlign = align;
  context.textBaseline = "alphabetic";
  context.fillText(line.text, x, baseline);
}

/** Dark modules as one rectangle per horizontal run, on whole pixels. */
function drawQr(
  context: CanvasRenderingContext2D,
  matrix: QrMatrix,
  left: number,
  top: number,
  modulePx: number,
): void {
  for (let row = 0; row < matrix.size; row++) {
    let column = 0;
    while (column < matrix.size) {
      if (!matrix.modules[row]?.[column]) {
        column++;
        continue;
      }
      const start = column;
      while (column < matrix.size && matrix.modules[row]?.[column]) {
        column++;
      }
      context.fillRect(
        left + start * modulePx,
        top + row * modulePx,
        (column - start) * modulePx,
        modulePx,
      );
    }
  }
}

/**
 * Line breaks as the browser makes them on the card: at spaces and after a
 * hyphen ("Oberhausen-" / "Wittgenstein"); a part still wider than the column
 * breaks anywhere, as `wrap-anywhere` does.
 */
function wrap(
  context: CanvasRenderingContext2D,
  font: string,
  text: string,
  maxWidth: number,
): string[] {
  context.font = font;
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.trim().split(/\s+/).filter(Boolean)) {
      // No lookbehind split: Safari before 16.4 rejects the whole module on it.
      (word.match(/[^-]+-*|-+/g) ?? [word]).forEach((part, index) => {
        const candidate = line + (line && index === 0 ? " " : "") + part;
        if (context.measureText(candidate).width <= maxWidth) {
          line = candidate;
          return;
        }
        if (line) {
          lines.push(line);
        }
        const pieces = breakAnywhere(context, part, maxWidth);
        lines.push(...pieces.slice(0, -1));
        line = pieces.at(-1) ?? "";
      });
    }
    if (line) {
      lines.push(line);
    }
  }
  return lines;
}

/** The e-mail on one line if it fits, else broken before the "@" first (the card's `<wbr>`). */
function wrapEmail(
  context: CanvasRenderingContext2D,
  font: string,
  [local, domain]: [string, string],
  maxWidth: number,
): string[] {
  if (!local) {
    return [];
  }
  context.font = font;
  const whole = domain ? `${local}@${domain}` : local;
  if (context.measureText(whole).width <= maxWidth) {
    return [whole];
  }
  return domain
    ? [...wrap(context, font, local, maxWidth), ...wrap(context, font, `@${domain}`, maxWidth)]
    : wrap(context, font, local, maxWidth);
}

function breakAnywhere(
  context: CanvasRenderingContext2D,
  word: string,
  maxWidth: number,
): string[] {
  const pieces: string[] = [];
  let piece = "";
  for (const char of word) {
    if (piece && context.measureText(piece + char).width > maxWidth) {
      pieces.push(piece);
      piece = char;
    } else {
      piece += char;
    }
  }
  if (piece) {
    pieces.push(piece);
  }
  return pieces;
}
